/* eslint-disable no-await-in-loop -- need to execute sequentially */
import type { EmitActionParams } from '@unifyapps/defs/hooks/useBlockAction';
import type { ActionContext, TriggerEventParams } from '@unifyapps/defs/hooks/useBlockEvents';
import type { Action } from '@unifyapps/defs/types/action';
import type { BlockId } from '@unifyapps/defs/types/block';
import type { ConditionEvaluationObject } from '@unifyapps/defs/types/conditionEvaluationObject';
import type { Event, EventTypeEnum } from '@unifyapps/defs/types/event';
import type { SyntheticEvent } from '@unifyapps/defs/types/syntheticEvent';
import { debugNoCodeInteractions } from '../../consts/debug';
import {
  DebounceResetError,
  executeWithDebounceThrottle,
} from '../../utils/debounceThrottleExecutor';
import { getIdWithPrefix } from '../../utils/id';
import { EVENT_PREFIX } from '../const';
import type { OnActionType } from '../context/ActionsProvider/context';
import { SkipActionHandlersError } from '../hooks/useGlobalBlockActions/const';
import type { ActionTrigger, EventHandlerObserver } from './observer';
import { getActionContext } from './utils/getActionContext';
import { getEventsToTrigger } from './utils/getEventsToTrigger';

export const BLOCK_TARGET_ID = 'BASE_BLOCK';

class NoCodeEventHandler {
  // {
  //   '<targetId>': {
  //     <eventType> : [{
  //       ...event
  //     }]
  //   }
  // }
  private events: Record<string, Record<string, Event[] | undefined> | undefined>;
  private processing: Record<string, Record<string, boolean> | undefined>;
  private eventQueues: Record<string, Record<string, TriggerEventParams[]>>;
  private doAction: OnActionType;
  private readonly evaluateConditionalOperator: (params: {
    conditionEvaluationObj: ConditionEvaluationObject | undefined;
    context: Record<string, unknown>;
  }) => boolean | undefined;
  private readonly getComputeContext: () => Record<string, unknown>;
  private eventsGateway: {
    canTriggerEvents: boolean;
    whitelistedEvents: EventTypeEnum[];
  } | null;
  private waitingQueue: (TriggerEventParams & { id: string })[];
  private filterWaitingEvents?: (events: TriggerEventParams[]) => TriggerEventParams[];
  private onAddWaitingEventCallback?: (eventId: string) => void;
  // Telemetry side-channel. Undefined in the matrix runner (no Pulse), so every tracking call
  // is a single nullish check away from doing nothing. All Pulse knowledge lives in the observer.
  private readonly observer?: EventHandlerObserver;

  constructor({
    evaluateConditionalOperator,
    getComputeContext,
    onAddWaitingEventCallback,
    filterWaitingEvents,
    observer,
  }: {
    evaluateConditionalOperator: NoCodeEventHandler['evaluateConditionalOperator'];
    getComputeContext: NoCodeEventHandler['getComputeContext'];
    onAddWaitingEventCallback?: (eventId: string) => void;
    filterWaitingEvents?: (events: TriggerEventParams[]) => TriggerEventParams[];
    observer?: EventHandlerObserver;
  }) {
    this.events = {};
    this.eventQueues = {};
    this.processing = {};
    this.waitingQueue = [];
    this.eventsGateway = null;
    this.evaluateConditionalOperator = evaluateConditionalOperator;
    this.getComputeContext = getComputeContext;
    this.onAddWaitingEventCallback = onAddWaitingEventCallback;
    this.filterWaitingEvents = filterWaitingEvents;
    this.observer = observer;
  }

  reset() {
    this.events = {};
    this.eventQueues = {};
    this.processing = {};
    this.waitingQueue = [];
  }

  registerQueueEventType(targetId: string) {
    this.eventQueues[targetId] = {};
    const eventTypes = Object.keys(this.events[targetId] || {});
    const eventTypesLength = eventTypes.length;
    for (let i = 0; i < eventTypesLength; i++) {
      this.eventQueues[targetId][eventTypes[i]] = [];
    }
  }

  registerEvents(events: Event[]) {
    const eventsLength = events.length;
    this.events = {};
    for (let i = 0; i < eventsLength; i++) {
      const event = events[i];
      this.registerEvent(event);
      this.registerQueueEventType(event.targetId || BLOCK_TARGET_ID);
    }
  }

  registerEvent(event: Event) {
    const eventType = event.eventType;
    const targetId = event.targetId || BLOCK_TARGET_ID;

    // Avoid object spread in hot path - use direct assignment
    if (!this.events[targetId]) {
      this.events[targetId] = {};
    }
    if (!this.events[targetId][eventType]) {
      this.events[targetId][eventType] = [];
    }
    this.events[targetId][eventType].push(event);

    this.setEventToProcessing(eventType, targetId, false);
  }

  getEventsByType(
    eventType: string,
    targetId: string,
    actionContext: EmitActionParams['actionContext'],
    domEvent?: SyntheticEvent,
    eventPayload?: Record<string, unknown>,
  ) {
    const events = this.events[targetId]?.[eventType] || [];

    return getEventsToTrigger({
      events,
      actionContext,
      eventType,
      domEvent,
      eventPayload,
    });
  }

  setEventToProcessing(eventType: string, targetId: string, processing: boolean) {
    // Avoid object spread in hot path - use direct assignment
    if (!this.processing[targetId]) {
      this.processing[targetId] = {};
    }
    this.processing[targetId][eventType] = processing;
  }

  canEventBeTriggered(eventType: string) {
    if (this.eventsGateway?.canTriggerEvents) {
      return true;
    }

    return this.eventsGateway?.whitelistedEvents.includes(eventType as EventTypeEnum);
  }

  triggerEvent = async (params: TriggerEventParams) => {
    const { eventType, doAction, actionContext, blockId, domEvent, eventPayload } = params;
    const targetId = params.targetId || BLOCK_TARGET_ID;
    const events = this.getEventsByType(
      eventType,
      targetId,
      actionContext,
      domEvent,
      eventPayload as Record<string, unknown> | undefined,
    );

    if (!events.length) {
      debugNoCodeInteractions(`Event ${eventType} not registered for targetId: ${targetId}`);
      return;
    }

    // If canTriggerEvents is false, push to waiting queue instead of processing immediately
    if (!this.canEventBeTriggered(eventType)) {
      const id = getIdWithPrefix(EVENT_PREFIX);
      this.waitingQueue.push({ ...params, id });
      this.onAddWaitingEventCallback?.(id);
      return;
    }

    this.observer?.onEvent(params);

    this.eventQueues[targetId][eventType].push(params);

    if (!this.processing[targetId]?.[eventType]) {
      await this.processEventQueue(targetId, eventType, doAction, blockId);
    }
  };

  private async processEventQueue(
    targetId: string,
    eventType: string,
    doAction: OnActionType,
    _blockId?: BlockId,
  ) {
    try {
      this.setEventToProcessing(eventType, targetId, true);
      this.doAction = doAction;

      while (this.eventQueues[targetId][eventType].length > 0) {
        const params = this.eventQueues[targetId][eventType].shift();
        if (params) await this.executeEvent(params);
      }
    } catch (error) {
      // NOTE: intentionally not reporting to Sentry / logError here — it would add noise. This catch
      // also covers expected paths (e.g. form validation) and deliberate throws used to invoke onErrorAction,
      // not only unexpected failures.
      // logError(error);
      console.error(error);
    } finally {
      this.setEventToProcessing(eventType, targetId, false);

      // Check if there are any new events in the queue
      if (this.eventQueues[targetId][eventType].length > 0) {
        await this.processEventQueue(targetId, eventType, doAction);
      }
    }
  }

  private async executeEvent(params: TriggerEventParams) {
    const { eventType, actionContext, domEvent, eventPayload } = params;
    const targetId = params.targetId || BLOCK_TARGET_ID;
    const events = this.getEventsByType(
      eventType,
      targetId,
      actionContext,
      domEvent,
      eventPayload as Record<string, unknown> | undefined,
    );

    let adaptedActionContext: ActionContext;
    if (eventPayload) {
      adaptedActionContext = { ...actionContext, eventPayload } as ActionContext;
    } else {
      adaptedActionContext = (actionContext || {}) as ActionContext;
    }

    /**
     * Execution strategy for actions in an event:
     *
     * Each action evaluates its executionType INDEPENDENTLY.
     * - delay / undefined: awaited sequentially (blocks until done)
     * - debounce: fire-and-forget with its own timer (per action.id). Rapid events
     *   reset the timer; only fires after triggers stop for delayDuration ms.
     * - throttle: fire-and-forget with its own timer (per action.id). Fires immediately
     *   on first call, then at most once per delayDuration ms window.
     *
     * Cancellation: when a new event calls executeWithDebounceThrottle with the same
     * action.id, the previous pending promise is rejected with DebounceResetError.
     * This is silent — the .catch() handles it.
     *
     * Example: E has A1 (throttle 10s), A2 (debounce 500ms), A3 (normal)
     *   E1 at t=0:   A1 fires immediately, A2 timer starts, A3 fires immediately
     *   E2 at t=100: A1 suppressed (in window), A2 timer resets, A3 fires immediately
     *   t=600:       A2 fires (500ms after last trigger)
     *   t=10000:     A1 fires (throttle window expired, queued call)
     *
     * Success/failure handlers (onSuccessActions/onErrorActions) run inside the
     * debounce/throttle callback — they execute after the action fires, not immediately.
     */
    const eventsToProcess = events.length;
    for (let i = 0; i < eventsToProcess; i++) {
      const { action } = events[i];
      const executionType = action.executionType;

      if (executionType === 'debounce' || executionType === 'throttle') {
        void executeWithDebounceThrottle(
          action.id || '',
          executionType,
          action.delayDuration ?? 0,
          async () => {
            await this.executeAction({
              action: { ...action, executionType: undefined, delayDuration: undefined },
              actionContext: adaptedActionContext,
              domEvent,
              blockId: params.blockId,
              anchorId: params.anchorId,
              instanceId: params.instanceId,
            });
          },
        ).catch((error) => {
          if (error instanceof DebounceResetError) {
            this.observer?.onActionSkipped(
              { action, blockId: params.blockId, instanceId: params.instanceId },
              'debounceReset',
            );
            debugNoCodeInteractions(`Action ${action.id} reset by newer event`);
            return;
          }
          // NOTE: intentionally not reporting to Sentry / logError here — it would add noise. This catch
          // also covers expected paths (e.g. form validation) and deliberate throws used to invoke onErrorAction,
          // not only unexpected failures.
          // logError(error);
          console.error(error);
        });
        continue;
      }

      await this.executeAction({
        action,
        actionContext: adaptedActionContext,
        domEvent,
        blockId: params.blockId,
        anchorId: params.anchorId,
        instanceId: params.instanceId,
      });
    }
  }

  async executeActions(
    actions: Action[],
    emitActionParams: Partial<EmitActionParams>,
    pulseTrigger?: ActionTrigger,
  ) {
    const actionsLength = actions.length;
    for (let i = 0; i < actionsLength; i++) {
      await this.executeAction({ ...emitActionParams, action: actions[i] }, pulseTrigger);
    }
  }

  async executeAction(params: EmitActionParams, pulseTrigger?: ActionTrigger) {
    const { action, actionContext } = params;
    try {
      // skip disabled actions
      if (action.disabled) {
        debugNoCodeInteractions(`Action ${action.id} is disabled, skipping`);
        this.observer?.onActionSkipped(params, 'disabled', pulseTrigger);
        return;
      }

      const computedContext = this.getComputeContext();

      const shouldRun = this.evaluateConditionalOperator({
        conditionEvaluationObj: action.runCondition,
        context: getActionContext(computedContext, actionContext),
      });

      if (shouldRun === false) {
        debugNoCodeInteractions(
          `action run condition (determined by filters set in only run when field) not met, skipping action ${action.actionType}`,
        );
        this.observer?.onActionSkipped(params, 'runCondition', pulseTrigger);
        return;
      }

      if (typeof action.delayDuration === 'number') {
        await new Promise((resolve) => {
          setTimeout(resolve, action.delayDuration);
        });
      }

      const computeContext = this.getComputeContext();

      // notify before doAction so the action row appears first in the Pulse stream, ahead of
      // any stateChange events that doAction synchronously dispatches.
      this.observer?.onActionStart(params, pulseTrigger);
      await this.doAction({
        ...params,
        actionContext: getActionContext(computeContext, actionContext),
      });

      if (action.onSuccessActions) {
        await this.executeActions(
          action.onSuccessActions,
          {
            actionContext: params.actionContext,
            domEvent: params.domEvent,
            instanceId: params.instanceId,
            blockId: params.blockId,
            anchorId: params.anchorId,
          },
          'onSuccess',
        );
      }
    } catch (error) {
      // Used to skip success and error handlers
      // e.g. when user cancels file picker, we don't want to trigger error handlers - useControlMediaUploads.ts
      if (error instanceof SkipActionHandlersError) {
        return;
      }
      this.observer?.onActionError(params, error);
      debugNoCodeInteractions(`Error executing action ${params.action.actionType}:`, error);
      // NOTE: intentionally not reporting to Sentry / logError here — it would add noise. This catch
      // also covers expected paths (e.g. form validation) and deliberate throws used to invoke onErrorAction,
      // not only unexpected failures.
      // logError(error);
      console.error(error);
      if (params.action.onErrorActions) {
        await this.executeActions(
          params.action.onErrorActions,
          {
            actionContext: params.actionContext,
            domEvent: params.domEvent,
            instanceId: params.instanceId,
            blockId: params.blockId,
            anchorId: params.anchorId,
          },
          'onError',
        );
      }
    }
  }

  getIsProcessingEvent = (targetId: string) => {
    const processingTarget = this.processing[targetId];
    if (!processingTarget) return false;

    const eventTypes = Object.keys(processingTarget);
    const eventTypesLength = eventTypes.length;
    for (let i = 0; i < eventTypesLength; i++) {
      if (processingTarget[eventTypes[i]]) {
        return true;
      }
    }
    return false;
  };

  async processWaitingEvents() {
    if (this.waitingQueue.length === 0) {
      return;
    }

    const eventsToProcess = this.filterWaitingEvents
      ? this.filterWaitingEvents(this.waitingQueue.slice())
      : this.waitingQueue.slice();

    this.waitingQueue = [];
    const eventsLength = eventsToProcess.length;
    for (let i = 0; i < eventsLength; i++) {
      await this.triggerEvent(eventsToProcess[i]);
    }
  }

  setEventsGateway(eventsGateway: {
    canTriggerEvents: boolean;
    whitelistedEvents: EventTypeEnum[];
  }) {
    this.eventsGateway = eventsGateway;
  }

  getCanTriggerEvents() {
    return this.eventsGateway?.canTriggerEvents;
  }
}

export default NoCodeEventHandler;
